import { useState, useEffect, useMemo, useCallback } from 'react';
import styles from './school.module.css';
import axios from 'axios';
import Notification from '../../shared/Notification';

// ─────────────────────────────────────────────────────────────────────────────
// constants
// ─────────────────────────────────────────────────────────────────────────────
const GRADE_OPTIONS = [
    'Kindergarten', 'Pre-K',
    '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12',
    '1st Year', '2nd Year', '3rd Year', '4th Year',
    'Freshman', 'Sophomore', 'Junior', 'Senior',
    'Masters Year 1', 'Masters Year 2', 'PhD Year 1', 'PhD Year 2',
];

const SECTION_PRESETS = [
    { label: 'A – D', values: ['A', 'B', 'C', 'D'] },
    { label: 'A – F', values: ['A', 'B', 'C', 'D', 'E', 'F'] },
    { label: '1 – 4', values: ['1', '2', '3', '4'] },
    { label: 'Greek', values: ['Alpha', 'Beta', 'Gamma', 'Delta'] },
];

// ─────────────────────────────────────────────────────────────────────────────
// small helpers
// ─────────────────────────────────────────────────────────────────────────────
const buildSchoolId = (school) =>
    school.id || `${school.name}__${school.country}`.toLowerCase().replace(/\s+/g, '_');

async function loadCustomSchools() {
    try {
        const result = await window.storage.get('custom-schools', true);
        return result?.value ? JSON.parse(result.value) : [];
    } catch {
        return [];
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// School (main component)
// ─────────────────────────────────────────────────────────────────────────────
const GuestSchool = () => {

    // ── data ────────────────────────────────────────────────────────────────
    const [mySchools,       setMySchools]       = useState([]);   // always an array
    const [filteredSchools, setFilteredSchools] = useState([]);

    // ── UI state ─────────────────────────────────────────────────────────────
    const [searchTerm,   setSearchTerm]   = useState('');
    const [searching,    setSearching]    = useState(false);
    const [saving,       setSaving]       = useState(false);
    const [error,        setError]        = useState('');
    const [success,      setSuccess]      = useState('');

    // modals / panels
    const [showAddModal,   setShowAddModal]   = useState(false);
    const [classPanelFor,  setClassPanelFor]  = useState(null);  // schoolId being configured
    const [membersPanel,   setMembersPanel]   = useState(null);  // { schoolId, class, section }

    // add-school form
    const [newName,    setNewName]    = useState('');
    const [newType,    setNewType]    = useState('university');
    const [newCountry, setNewCountry] = useState('');

    // ── feedback helpers ─────────────────────────────────────────────────────
    const flash = useCallback((setter, msg, ms = 3000) => {
        setter(msg);
        setTimeout(() => setter(''), ms);
    }, []);

    const [notification, setNotification] = useState('');

    // ── load my schools on mount ─────────────────────────────────────────────
    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get('/api/guest/school/get-school');
                setMySchools(Array.isArray(data.schools) ? data.schools : []);
            } catch (err) {
                // console.error('load schools:', err);
            }
        })();
    }, []);

    // ── search (debounced, 500 ms) ───────────────────────────────────────────
    useEffect(() => {
        if (!searchTerm || searchTerm.trim().length < 2) {
            setFilteredSchools([]);
            return;
        }

        const timer = setTimeout(async () => {
            setSearching(true);
            try {
                const [apiRes, custom] = await Promise.all([
                    fetch(`https://universities.hipolabs.com/search?name=${encodeURIComponent(searchTerm)}`)
                        .then(r => r.ok ? r.json() : []),
                    loadCustomSchools()
                ]);

                const apiSchools = apiRes.map(s => ({
                    id:      `api-${s.name}-${s.country}`,
                    name:    s.name,
                    type:    'university',
                    country: s.country,
                    website: s.web_pages?.[0] || '',
                    domain:  s.domains?.[0]   || '',
                    source:  'api'
                }));

                const filteredCustom = custom.filter(s =>
                    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    s.country.toLowerCase().includes(searchTerm.toLowerCase())
                );

                // merge + deduplicate
                const merged = [...filteredCustom, ...apiSchools].filter((s, i, arr) =>
                    arr.findIndex(x =>
                        x.name.toLowerCase() === s.name.toLowerCase() &&
                        x.country.toLowerCase() === s.country.toLowerCase()
                    ) === i
                );

                setFilteredSchools(merged);
            } catch {
                const custom = await loadCustomSchools();
                setFilteredSchools(custom.filter(s =>
                    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    s.country.toLowerCase().includes(searchTerm.toLowerCase())
                ));
            } finally {
                setSearching(false);
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    // ── save school to DB (adds to schools array) ────────────────────────────
    const saveSchoolToDB = useCallback(async (schoolData) => {
        setSaving(true);
        try {
            const { data } = await axios.put('/api/guest/school/update-school',
                { school: schoolData }
            );
            setMySchools(Array.isArray(data.schools) ? data.schools : []);
            flash(setSuccess, 'School saved!');
            return true;
        } catch {
            flash(setError, 'Failed to save school.');
            return false;
        } finally {
            setSaving(false);
        }
    }, [flash]);

    // ── update class/section for an existing school ──────────────────────────
    const saveClassSection = useCallback(async (schoolId, cls, section) => {
        setSaving(true);
        try {
            const { data } = await axios.put('/api/guest/school/update-school-class-section',
                { schoolId, class: cls, section });
            setMySchools(Array.isArray(data.schools) ? data.schools : []);
            flash(setSuccess, 'Class & section saved!');
        } catch {
            flash(setError, 'Failed to update class/section.');
        } finally {
            setSaving(false);
        }
    }, [ flash]);

    // ── remove school ────────────────────────────────────────────────────────
    const removeSchool = useCallback(async (schoolId) => {
        setSaving(true);
        try {
            await axios.delete(`/api/guest/school/remove-school/${schoolId}`);
            setMySchools(prev => prev.filter(s => s.schoolId !== schoolId));
            flash(setSuccess, 'School removed.');
        } catch {
            flash(setError, 'Failed to remove school.');
        } finally {
            setSaving(false);
        }
    }, [flash]);

    // ── select a school from search results ──────────────────────────────────
    const handleSelectSchool = useCallback(async (school) => {
        const alreadyAdded = mySchools.some(s => s.schoolId === buildSchoolId(school));
        if (alreadyAdded) {
            flash(setError, 'This school is already in your profile.');
            return;
        }
        const saved = await saveSchoolToDB({
            id:      buildSchoolId(school),
            name:    school.name,
            type:    school.type,
            country: school.country,
            website: school.website || '',
            domain:  school.domain  || '',
            source:  school.source  || 'api',
            class:   '',
            section: ''
        });
        if (saved) setSearchTerm('');
    }, [mySchools, saveSchoolToDB, flash]);

    // ── add custom school ─────────────────────────────────────────────────────
    const handleAddCustomSchool = useCallback(async () => {
        if (!newName.trim() || !newCountry.trim()) {
            setError('Name and Country are required.');
            return;
        }
        setError('');

        const custom = await loadCustomSchools();
        const dupe = custom.find(s =>
            s.name.toLowerCase().trim()    === newName.toLowerCase().trim() &&
            s.country.toLowerCase().trim() === newCountry.toLowerCase().trim()
        );
        if (dupe) { setError('This school already exists.'); return; }

        const newSchool = {
            id:      `custom-${Date.now()}`,
            name:    newName.trim(),
            type:    newType,
            country: newCountry.trim(),
            source:  'custom'
        };

        await window.storage.set('custom-schools', JSON.stringify([...custom, newSchool]), true);
        setShowAddModal(false);
        setNewName(''); setNewType('university'); setNewCountry('');
        await handleSelectSchool(newSchool);
    }, [newName, newType, newCountry, handleSelectSchool]);

    // ─────────────────────────────────────────────────────────────────────────
    // render
    // ─────────────────────────────────────────────────────────────────────────
    const showSearch    = mySchools.length === 0 || searchTerm;
    const hasMySchools  = mySchools.length > 0;

    const handleCloseNotification = () => {
        setNotification('');
    };
    return (
        <div className={styles.container}>

            {/* ── header ── */}
            <div className={styles.header}>
                <h2>{hasMySchools ? 'My Schools' : 'Find Your School'}</h2>
                {saving && <span className={styles.savingIndicator}>Saving…</span>}
            </div>
        {notification && (
                        <Notification message={notification} onClose={handleCloseNotification} />
                    )}
            {/* ── feedback ── */}
            {success && <div className={styles.success}>{success}</div>}
            {error   && <div className={styles.error}>{error}</div>}

            {/* ── my schools list ── */}
            {hasMySchools && (
                <div className={styles.mySchoolsList}>
                    {mySchools.map(s => (
                        <SchoolCard
                            key={s.schoolId}
                            school={s}
                            onRemove={() => setNotification('Please Signup to Remove the School') }
                            onEditClassSection={() => setClassPanelFor(s.schoolId)}
                            onViewMembers={(grain) => setMembersPanel({ schoolId: s.schoolId, ...grain })}
                        />
                    ))}
                    {/* <button
                        className={styles.addMoreBtn}
                        onClick={() => setSearchTerm(' ')}  
                    >
                        + Add Another School
                    </button> */}
                </div>
            )}

            {/* ── search ── */}
            {(showSearch || !hasMySchools) && (
                <div className={styles.searchContainer}>
                    <input
                        type="text"
                        className={styles.searchInput}
                        placeholder="Search schools, colleges, universities…"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        autoFocus={!hasMySchools}
                    />
                    {searching && <span className={styles.searchingIndicator}>Searching…</span>}
                </div>
            )}

            {/* ── search results ── */}
            {searchTerm.trim().length >= 2 && (
                <>
                    {filteredSchools.length > 0 && (
                        <div className={styles.resultsContainer}>
                            <div className={styles.resultsHeader}>
                                {filteredSchools.length} result{filteredSchools.length !== 1 ? 's' : ''}
                            </div>
                            <div className={styles.resultsList}>
                                {filteredSchools.map(school => {
                                    const sid    = buildSchoolId(school);
                                    const added  = mySchools.some(s => s.schoolId === sid);
                                    return (
                                        <div
                                            key={school.id}
                                            className={`${styles.resultItem} ${added ? styles.resultAdded : ''}`}
                                            onClick={() => !added && handleSelectSchool(school)}
                                        >
                                            <div className={styles.schoolName}>{school.name}</div>
                                            <div className={styles.schoolMeta}>
                                                <span className={styles.schoolType}>{school.type}</span>
                                                <span className={styles.schoolCountry}>{school.country}</span>
                                                {school.source === 'custom' && (
                                                    <span className={styles.customBadge}>Custom</span>
                                                )}
                                                {added && <span className={styles.addedBadge}>✓ Added</span>}
                                            </div>
                                            {school.website && (
                                                <a
                                                    href={school.website}
                                                    className={styles.schoolWebsite}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={e => e.stopPropagation()}
                                                >
                                                    {school.website}
                                                </a>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {!searching && filteredSchools.length === 0 && (
                        <div className={styles.noResults}>
                            <div className={styles.noResultsIcon}>🔍</div>
                            <h3>No schools found for "{searchTerm}"</h3>
                            <button
                                className={styles.addBtn}
                                onClick={() => { setNewName(searchTerm); setShowAddModal(true); }}
                            >
                                + Add "{searchTerm}" to database
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* ── empty state ── */}
            {!hasMySchools && !searchTerm && (
                <div className={styles.emptyState}>
                    <div className={styles.emptyIcon}>🎓</div>
                    <h3>Find your school</h3>
                    <p>Search thousands of schools and universities worldwide</p>
                </div>
            )}

            {/* ── class / section panel ── */}
            {classPanelFor && (
                <ClassSectionPanel
                    school={mySchools.find(s => s.schoolId === classPanelFor)}
                    onSave={(cls, sec) => {
                        saveClassSection(classPanelFor, cls, sec);
                        setClassPanelFor(null);
                    }}
                    onClose={() => setClassPanelFor(null)}
                />
            )}

            {/* ── members browser ── */}
            {membersPanel && (
                <MembersPanel
                    // token={token}
                    schoolId={membersPanel.schoolId}
                    schoolName={mySchools.find(s => s.schoolId === membersPanel.schoolId)?.name}
                    initialClass={membersPanel.class}
                    initialSection={membersPanel.section}
                    onClose={() => setMembersPanel(null)}
                />
            )}

            {/* ── add custom school modal ── */}
            {showAddModal && (
                <div className={styles.modal} onClick={() => setShowAddModal(false)}>
                    <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3>Add New School</h3>
                            <button className={styles.closeBtn} onClick={() => setShowAddModal(false)}>×</button>
                        </div>

                        <div className={styles.formGroup}>
                            <label>School Name *</label>
                            <input
                                className={styles.input}
                                value={newName}
                                onChange={e => setNewName(e.target.value)}
                                placeholder="e.g. Yale University"
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label>Type *</label>
                            <select className={styles.input} value={newType} onChange={e => setNewType(e.target.value)}>
                                <option value="school">School</option>
                                <option value="college">College</option>
                                <option value="university">University</option>
                            </select>
                        </div>

                        <div className={styles.formGroup}>
                            <label>Country *</label>
                            <input
                                className={styles.input}
                                value={newCountry}
                                onChange={e => setNewCountry(e.target.value)}
                                placeholder="e.g. United States"
                            />
                        </div>

                        {error && <div className={styles.modalError}>{error}</div>}

                        <div className={styles.modalActions}>
                            <button className={styles.cancelBtn} onClick={() => setShowAddModal(false)}>Cancel</button>
                            <button className={styles.submitBtn} onClick={handleAddCustomSchool}>Add School</button>
                        </div>

                        <p className={styles.modalInfo}>
                            This school will be available to all users globally once added.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// SchoolCard
// ─────────────────────────────────────────────────────────────────────────────
const SchoolCard = ({ school, onRemove, onEditClassSection, onViewMembers }) => (
    <div className={styles.schoolCard}>
        <div className={styles.schoolCardMain}>
            <div className={styles.schoolCardName}>{school.name}</div>
            <div className={styles.schoolCardMeta}>
                <span className={styles.schoolType}>{school.type}</span>
                <span className={styles.schoolCountry}>{school.country}</span>
                {school.source === 'custom' && <span className={styles.customBadge}>Custom</span>}
            </div>

            {(school.class || school.section) && (
                <div className={styles.schoolCardClassSection}>
                    {school.class   && <span>Grade / Class: <strong>{school.class}</strong></span>}
                    {school.section && <span>  Section: <strong>{school.section}</strong></span>}
                </div>
            )}
        </div>

        <div className={styles.schoolCardActions}>
            <button
                className={styles.actionBtn}
                onClick={() => onEditClassSection()}
                title="Set class & section"
            >
                {school.class ? '✏️ Edit Class' : '+ Class/Section'}
            </button>

            <button
                className={styles.actionBtn}
                onClick={() => onViewMembers({ class: '', section: '' })}
                title="Browse school members"
            >
                👥 Members
            </button>

            {school.class && (
                <button
                    className={styles.actionBtn}
                    onClick={() => onViewMembers({ class: school.class, section: '' })}
                    title="Browse classmates"
                >
                    🏫 Classmates
                </button>
            )}

            {school.class && school.section && (
                <button
                    className={styles.actionBtn}
                    onClick={() => onViewMembers({ class: school.class, section: school.section })}
                    title="Browse section"
                >
                    🪪 Section {school.section}
                </button>
            )}

            <button
                className={`${styles.actionBtn} ${styles.removeBtn}`}
                onClick={onRemove}
                title="Remove school"
            >
                ✕ Remove
            </button>
        </div>
    </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// ClassSectionPanel  –  set / change class & section for one school
// ─────────────────────────────────────────────────────────────────────────────
const ClassSectionPanel = ({ school, onSave, onClose }) => {
    const [cls,     setCls]     = useState(school?.class   || '');
    const [section, setSection] = useState(school?.section || '');
    const [customSection, setCustomSection] = useState('');
    const [notification, setNotification] = useState('');

    const sectionOptions = useMemo(() => {
        // Build from presets that include the current value
        const base = ['A','B','C','D','E','F','G','H','Alpha','Beta','Gamma','Delta',
                       '1','2','3','4','5','6'];
        if (section && !base.includes(section)) base.unshift(section);
        return base;
    }, [section]);

    const handleSave = () => {
        const finalSection = customSection.trim() || section;
        setNotification('Please Signup to Change the Class');
        // onSave(cls, finalSection);
    };
    const handleCloseNotification = () => {
        setNotification('');
    }

    return (
        <div className={styles.modal} onClick={onClose}>
            <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
                {notification && (
                        <Notification message={notification} onClose={handleCloseNotification} />
                    )}
                <div className={styles.modalHeader}>
                    <h3>Class & Section — {school?.name}</h3>
                    <button className={styles.closeBtn} onClick={onClose}>×</button>
                </div>

                {/* ── class / grade ── */}
                <div className={styles.formGroup}>
                    <label>Class / Grade</label>
                    <select
                        className={styles.input}
                        value={cls}
                        onChange={e => setCls(e.target.value)}
                    >
                        <option value="">— select —</option>
                        {GRADE_OPTIONS.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                </div>

                {/* ── section ── */}
                <div className={styles.formGroup}>
                    <label>Section</label>

                    {/* quick presets */}
                    <div className={styles.sectionPresets}>
                        {SECTION_PRESETS.map(p => (
                            <button
                                key={p.label}
                                type="button"
                                className={styles.presetBtn}
                                onClick={() => {
                                    // pick the first in the group if none selected yet
                                    if (!section) setSection(p.values[0]);
                                }}
                                title={p.values.join(', ')}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>

                    <select
                        className={styles.input}
                        value={section}
                        onChange={e => { setSection(e.target.value); setCustomSection(''); }}
                    >
                        <option value="">— select —</option>
                        {sectionOptions.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>

                    <input
                        className={styles.input}
                        style={{ marginTop: 6 }}
                        placeholder="Or type a custom section…"
                        value={customSection}
                        onChange={e => setCustomSection(e.target.value)}
                    />
                </div>

                <div className={styles.modalActions}>
                    <button className={styles.cancelBtn} onClick={onClose}>Cancel</button>
                    <button className={styles.submitBtn} onClick={handleSave}>Save</button>
                </div>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// MembersPanel  –  browse people in a school / class / section
// ─────────────────────────────────────────────────────────────────────────────
const MembersPanel = ({  schoolId, schoolName, initialClass = '', initialSection = '', onClose }) => {
    const [cls,     setCls]     = useState(initialClass);
    const [section, setSection] = useState(initialSection);
    const [data,    setData]    = useState(null);
    const [loading, setLoading] = useState(false);
    const [page,    setPage]    = useState(1);

    const fetch = useCallback(async (c, s, p = 1) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page: p, limit: 20 });
            if (c) params.set('class', c);
            if (s) params.set('section', s);

            const { data: res } = await axios.get(
                `/api/guest/school/school-members/${schoolId}?${params}`);
            setData(prev => p === 1
                ? res
                : { ...res, members: [...(prev?.members || []), ...res.members] }
            );
        } catch (err) {
            // console.error('Error fetching members:', err);
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [schoolId]);

    useEffect(() => { fetch(cls, section, 1); setPage(1); }, [cls, section, fetch]);

    const label = section
        ? `Section ${section}` + (cls ? ` · ${cls}` : '')
        : cls
            ? `Class ${cls}`
            : schoolName || 'School';

    return (
        <div className={styles.modal} onClick={onClose}>
            <div
                className={`${styles.modalContent} ${styles.membersModal}`}
                onClick={e => e.stopPropagation()}
            >
                <div className={styles.modalHeader}>
                    <h3>👥 {label}</h3>
                    <button className={styles.closeBtn} onClick={onClose}>×</button>
                </div>

                {/* ── granularity filters ── */}
                <div className={styles.memberFilters}>
                    <select
                        className={styles.filterSelect}
                        value={cls}
                        onChange={e => { setCls(e.target.value); setSection(''); }}
                    >
                        <option value="">All classes</option>
                        {GRADE_OPTIONS.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>

                    {cls && (
                        <select
                            className={styles.filterSelect}
                            value={section}
                            onChange={e => setSection(e.target.value)}
                        >
                            <option value="">All sections</option>
                            {['A','B','C','D','E','F','1','2','3','4','Alpha','Beta','Gamma','Delta']
                                .map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                    )}
                </div>

                {/* ── member grid ── */}
                {loading && !data && <div className={styles.loadingMsg}>Loading…</div>}

                {data && (
                    <>
                        <div className={styles.memberCount}>
                            {data.total} member{data.total !== 1 ? 's' : ''}
                        </div>

                        <div className={styles.memberGrid}>
                            {data.members.map(m => (
                                <div key={m.applicantId} className={styles.memberCard} 
                                onClick={() => window.open(`/guest/profile/${m.applicantId}`, '_blank')}   >
                                    <div className={styles.memberAvatar}>
                                        {m.profilePicture
                                            ? <img src={m.profilePicture} alt={m.name} />
                                            : <span>{(m.name || '?')[0].toUpperCase()}</span>}
                                    </div>
                                    <div className={styles.memberName}>{m.name || 'Anonymous'}</div>
                                </div>
                            ))}
                        </div>

                        {data.page < data.pages && (
                            <button
                                className={styles.loadMoreBtn}
                                disabled={loading}
                                onClick={() => { const next = page + 1; setPage(next); fetch(cls, section, next); }}
                            >
                                {loading ? 'Loading…' : 'Load more'}
                            </button>
                        )}

                        {data.total === 0 && (
                            <p className={styles.emptyMembers}>No members found at this level.</p>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default GuestSchool;